import * as React from "react";

import DebounceInput from "react-debounce-input";

import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TableCell,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import CustomButton from "./CustomButton";

import { getRowFormValues } from "../../helper-functions/validation/objectValidation";

/**
 * Custom input component.
 * Text input field, dropdown list, button, or text.
 * Used in multiple forms.
 *
 * Required props:
 * name
 * row
 * onChange
 */
export default function CustomInput(props) {
    // Configuration.
    const name = props.name;
    const row = props.row;
    const onChange = props.onChange;
    const onIconClick = props.onIconClick;

    // Types of input.
    const select = props.select;
    const button = props.button;
    const checkbox = props.checkbox;
    const cfg = props.cfg; // Text input field with a settings icon, used for cfg bitmap.
    const svc = props.svc; // Text input field with a search icon, used for service pop-up.
    const onOff = props.switch; // Switch input field.
    const text = props.text;
    const inputType = props.type ? props.type : "text";

    // Style.
    const label = props.label;
    const placeholder = props.placeholder;
    const size = props.size ? props.size : "small";
    const align = props.align ? props.align : "center";
    const bold = props.bold ? props.bold : "inherit";
    const tooltip = props.tooltip ? props.tooltip : "";
    const textTransform = props.textTransform;
    const multiline = props.multiline ? props.multiline : false;
    const minRows = props.minRows;
    const maxRows = props.maxRows;
    const error = props.error;
    const helperText = props.helperText;
    const userDisabled = props.disabled;
    const adminDisabled = props.admin;
    const superuserDisabled = props.superuser;
    const hidden = props.hidden;

    // Misc.
    const index = props.index;
    const cell = props.cell;
    const fullWidth = props.noFullWidth ? false : true;
    const advanced = props.advanced;
    const pulse = props.pulse;

    const formError = row?.form?.[name]?.error ?? false;
    const formText = row?.form?.[name]?.helperText ?? "";

    // Password-related fields and helper functions.
    const [showPassword, setShowPassword] = React.useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const decidePasswordAdornment = () => {
        if (inputType === "password") {
            return <CustomButton icon={showPassword ? <Visibility /> : <VisibilityOff />} onClick={handleClickShowPassword} sx={{ p: 0 }} />;
        }
    };

    const decideFieldType = () => {
        if (inputType === "password") {
            return showPassword ? "text" : "password";
        }

        return inputType;
    };

    const decideDisabled = () => {
        let disabled = false;

        if (row?.form?.saved) {
            disabled = true;
        }

        if (adminDisabled && props.roles && !props.roles.isAdmin) {
            disabled = true;
        }

        if (superuserDisabled && props.roles && !props.roles.isSuperuser) {
            disabled = true;
        }

        if (advanced === false) {
            disabled = true;
        }

        return disabled || userDisabled;
    };

    const disabled = decideDisabled();
    const fieldType = decideFieldType();
    const endAdornment = props.endAdornment || decidePasswordAdornment();

    /**
     * Return the final component.
     */
    const returnComponent = () => {
        if (hidden) return;
        if (cell)
            return (
                <TableCell colSpan={props.colSpan} align={align}>
                    {createComponent()}
                </TableCell>
            );

        return createComponent();
    };

    /**
     * Decide which component to create.
     */
    const createComponent = () => {
        if (hidden) return;

        if (select) return selectComponent();
        if (button) return buttonComponent();
        if (checkbox) return checkboxComponent();
        if (cfg) return cfgComponent();
        if (svc) return inputWithIconComponent();
        if (onOff) return inputWithSwitchComponent();
        if (text !== undefined) return textComponent(); // "0" is falsy.

        return inputComponent();
    };

    const handleOnChange = (e) => {
        if (index === undefined) {
            onChange(row, name, e.target.value);
        } else {
            onChange(row[index], name, e.target.value);
        }
    };

    const handleIconClick = (e) => {
        if (index === undefined) {
            onIconClick(row, name, e.target.value);
        } else {
            onIconClick(row[index], name, e.target.value);
        }
    };

    const handleOnClick = (e) => {
        if (index === undefined) {
            onChange(row, name, e.target.checked);
        } else {
            onChange(row[index], name, e.target.checked);
        }
    };

    const handleButton = (e) => {
        if (index === undefined) {
            onChange(row);
        } else {
            onChange(index);
        }
    };

    const selectComponent = () => {
        const selection = typeof select === "function" ? select(row, name) : select;

        return (
            <FormControl fullWidth={fullWidth} error={formError}>
                <InputLabel>{label}</InputLabel>

                <Select value={row[name]} disabled={disabled} onChange={handleOnChange} size={size} label={label}>
                    <MenuItem value=""></MenuItem>
                    {selection.map((value) => (
                        <MenuItem key={value} value={value} disabled={props.validSelection ? props.validSelection(row, name) : false}>
                            {textTransform === "uppercase" && isNaN(value) ? value.toUpperCase() : value}
                        </MenuItem>
                    ))}
                </Select>

                <FormHelperText>{formText}</FormHelperText>
            </FormControl>
        );
    };

    const inputComponent = () => {
        return (
            <DebounceInput
                element={TextField}
                debounceTimeout={250}
                autoFocus={props.autoFocus}
                // Value.
                value={row[name]}
                disabled={disabled}
                onChange={handleOnChange}
                // Style.
                fullWidth={fullWidth}
                multiline={multiline}
                minRows={minRows}
                maxRows={maxRows}
                size={size}
                type={fieldType}
                // Input style.
                label={label}
                placeholder={placeholder}
                autoComplete={inputType !== "text" ? undefined : name} // Type="tel" doesn't work w/ autocomplete
                inputProps={{
                    step: props.step,
                    min: props.min,
                    max: props.max,
                    style: { paddingLeft: 6, paddingRight: 4, textTransform: textTransform },
                    pattern: props.pattern
                }}
                InputProps={{ startAdornment: props.startAdornment, endAdornment: endAdornment }}
                // Errors.
                error={error || formError}
                helperText={helperText || formText}
            />
        );
    };

    const textComponent = () => {
        const txt = row?.[name] ?? text;

        return <Typography style={{ whiteSpace: "pre-line", fontWeight: bold, marginTop: "10px", ...props.style }}>{txt}</Typography>;
    };

    const buttonComponent = () => {
        return (
            <Button onClick={handleButton} variant="outlined" disabled={!props.roles.isAdmin} size={size} sx={{ p: 1 }}>
                {text}
            </Button>
        );
    };

    const checkboxComponent = () => {
        return (
            <FormControl error={formError} variant="standard">
                <Checkbox checked={row[name]} onChange={handleOnClick} disabled={disabled} color={formError ? "error" : "primary"} size="small" />
                <FormHelperText sx={{ textAlign: "center" }}>{formText}</FormHelperText>
            </FormControl>
        );
    };

    const inputWithSwitchComponent = () => {
        return (
            <FormControl variant="standard">
                <FormGroup sx={{ alignContent: "center" }}>
                    <FormControlLabel
                        control={<Switch checked={row[name]} onChange={handleOnClick} color={formError ? "error" : "primary"} />}
                        label={label}
                        labelPlacement="start"
                        disabled={disabled}
                    />
                </FormGroup>
                <FormHelperText sx={{ alignSelf: "center" }}>{formText}</FormHelperText>
            </FormControl>
        );
    };

    const cfgComponent = () => {
        const color = getRowFormValues(row, name).error ? "red" : undefined;

        return (
            <TextField
                value={row[name]}
                disabled
                // Style.
                sx={{ minWidth: 70 }}
                multiline={multiline}
                size={size}
                type={inputType}
                // Input style.
                inputProps={{ style: { paddingLeft: 6 } }}
                InputProps={{
                    endAdornment: (
                        <Tooltip id={`bottom-tooltip-${tooltip.replaceAll(" ", "-")}`} title={tooltip} placement="bottom" arrow enterDelay={200} enterTouchDelay={200}>
                            <InputAdornment position="end" sx={{ m: 0, color, cursor: "pointer" }}>
                                <SettingsIcon onClick={handleOnChange} color="primary" />
                            </InputAdornment>
                        </Tooltip>
                    ),
                    style: { paddingRight: 4 }
                }}
                // Errors.
                error={formError}
                helperText={formText}
            />
        );
    };

    const inputWithIconComponent = () => {
        return (
            <DebounceInput
                element={TextField}
                debounceTimeout={250}
                // Value.
                value={row[name]}
                disabled={disabled}
                onChange={handleOnChange}
                // Style.
                fullWidth={fullWidth}
                multiline={multiline}
                minRows={minRows}
                maxRows={maxRows}
                size={size}
                type={fieldType}
                // Input style.
                label={label}
                placeholder={placeholder}
                autoComplete={inputType !== "text" ? undefined : name} // Type="tel" doesn't work w/ autocomplete
                inputProps={{
                    style: { paddingLeft: 6, paddingRight: 4, textTransform: textTransform },
                    pattern: props.pattern
                }}
                InputProps={{
                    endAdornment: (
                        <Tooltip id={`bottom-tooltip-${tooltip.replaceAll(" ", "-")}`} title={tooltip} placement="bottom" arrow enterDelay={200} enterTouchDelay={200}>
                            <InputAdornment position="end" sx={{ m: 0, cursor: "pointer" }}>
                                <SearchIcon onClick={handleIconClick} color="primary" className={pulse ? "flashing-green pulse" : undefined} />
                            </InputAdornment>
                        </Tooltip>
                    ),
                    style: { paddingRight: 4 }
                }} // Errors.
                error={error || formError}
                helperText={helperText || formText}
            />
        );
    };

    return returnComponent();
}
